import Zousan from 'zousan';

import { shallowEqual } from './funcs.ts';

const noOpCallback = () => {};

type StateRecord = Record<string, unknown>;

export class StateObj<TState extends StateRecord = StateRecord> {
  state!: TState;
  callback: (state: TState) => void;

  constructor(initial = {} as TState) {
    this.init(initial);
    this.callback = noOpCallback;
  }

  init = (initial: TState) => {
    this.state = { ...initial };
  };

  setCallback = (fn: (state: TState) => void) => {
    this.callback = fn;
    return () => this.unsetCallback();
  };

  unsetCallback = () => {
    this.callback = noOpCallback;
  };

  update = (vals: Partial<TState>) => {
    this.state = { ...this.state, ...vals };
    this.callback(this.getState());
    return this.state;
  };

  getState = (): TState => ({ ...this.state });
}

export class StateObjMultiple<TState extends StateRecord = StateRecord> {
  state: TState;
  traceFlag: unknown;
  lastState: TState;
  callbacks: Set<(state: TState, prevState: TState) => void>;

  constructor(initial = {} as TState, traceFlag?: unknown) {
    this.state = { ...initial };
    this.traceFlag = traceFlag;
    this.lastState = {} as TState;
    this.callbacks = new Set();
  }

  addCallback = (fn: (state: TState, prevState: TState) => void) => {
    this.callbacks.add(fn);
    return () => this.rmCallback(fn);
  };

  rmCallback = (fn: (state: TState, prevState: TState) => void) => this.callbacks.delete(fn);

  getChanges = (vals: Partial<TState>) => {
    const changes: Record<string, { from: unknown; to: unknown }> = {};
    for (const key in vals) {
      if (vals[key] !== this.state[key]) {
        changes[key] = { from: this.state[key], to: vals[key] };
      }
    }
    const { stack } = Error('stack');
    return { changes, stack };
  };

  update = (vals: Partial<TState>) => {
    if (this.traceFlag) {
      console.log('STATE: ', this.getChanges(vals));
    }

    const newState = { ...this.state, ...vals };
    this.state = newState;
    Zousan.soon(this.doCallbacks());
    return newState;
  };

  // Multiple calls to update can occur before doCallbacks runs, so compare against the previous snapshot.
  doCallbacks = () => () => {
    if (shallowEqual(this.lastState, this.state)) {
      return;
    }
    this.callbacks.forEach(callback => callback(this.state, this.lastState));
    this.lastState = this.state;
  };

  getState = (): TState => ({ ...this.state });
}
